import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from './lib/axiosClient';

const STATUS_CONFIG = {
  received:      { label: 'Waiting',     icon: '📥', color: '#6366f1', bg: '#eef2ff' },
  diagnosed:     { label: 'Diagnosed',   icon: '🔍', color: '#f59e0b', bg: '#fffbeb' },
  approved:      { label: 'Approved',    icon: '✅', color: '#10b981', bg: '#ecfdf5' },
  in_progress:   { label: 'In Progress', icon: '🔧', color: '#3b82f6', bg: '#eff6ff' },
  quality_check: { label: 'QC Check',    icon: '🔬', color: '#8b5cf6', bg: '#f5f3ff' },
  ready:         { label: 'Ready!',      icon: '🏁', color: '#059669', bg: '#ecfdf5' },
};

export default function ShopQueue() {
  const { subdomain } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    fetchQueue();
    const interval = setInterval(fetchQueue, 30000); // Refresh every 30s
    const clock = setInterval(() => setTime(new Date()), 1000);
    return () => { clearInterval(interval); clearInterval(clock); };
  }, [subdomain]);

  const fetchQueue = async () => {
    try {
      const res = await axios.get(`/api/public/shop-queue/${subdomain}`);
      setData(res.data);
    } catch (e) {
      console.error('Failed to load queue:', e);
    }
    setLoading(false);
  };

  if (loading) return (
    <div style={S.loadingScreen}>
      <div style={{fontSize:48,marginBottom:16}}>🏍️</div>
      <div style={{fontSize:18,fontWeight:600}}>Loading Shop Queue...</div>
    </div>
  );

  if (!data) return (
    <div style={S.loadingScreen}>
      <div style={{fontSize:48,marginBottom:16}}>❌</div>
      <div style={{fontSize:18,fontWeight:600}}>Shop not found</div>
    </div>
  );

  const { queue, business_name, primary_color } = data;

  return (
    <div style={S.screen}>
      {/* Header */}
      <div style={{...S.header, background: primary_color || '#1e293b'}}>
        <div style={S.headerLeft}>
          <a href="/app" style={{color:'rgba(255,255,255,0.7)',fontSize:12,fontWeight:600,textDecoration:'none',display:'flex',alignItems:'center',gap:4,marginBottom:4}}>
            ← Dashboard
          </a>
          <div style={S.shopName}>🏍️ {business_name}</div>
          <div style={S.shopSub}>Service Queue</div>
        </div>
        <div style={S.headerRight}>
          <div style={S.clock}>{time.toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit' })}</div>
          <div style={S.date}>{time.toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' })}</div>
          <a href={'/site/' + subdomain} target="_blank" rel="noopener noreferrer"
            style={{color:'rgba(255,255,255,0.6)',fontSize:11,fontWeight:600,textDecoration:'none',marginTop:4}}>
            View Public Site ↗
          </a>
        </div>
      </div>

      {/* Queue Columns */}
      <div style={S.columns}>
        {/* Now Working */}
        <div style={S.column}>
          <div style={{...S.columnHeader, background:'#3b82f6'}}>
            <span style={S.columnIcon}>🔧</span>
            <span style={S.columnTitle}>Now Working</span>
            <span style={S.columnCount}>{queue.in_progress.length}</span>
          </div>
          <div style={S.columnBody}>
            {queue.in_progress.length === 0 && <div style={S.emptyCol}>No active jobs</div>}
            {queue.in_progress.map((wo, i) => (
              <QueueCard key={i} wo={wo} pulse />
            ))}
          </div>
        </div>

        {/* Ready for Pickup */}
        <div style={S.column}>
          <div style={{...S.columnHeader, background:'#059669'}}>
            <span style={S.columnIcon}>🏁</span>
            <span style={S.columnTitle}>Ready for Pickup</span>
            <span style={S.columnCount}>{queue.ready.length}</span>
          </div>
          <div style={S.columnBody}>
            {queue.ready.length === 0 && <div style={S.emptyCol}>None ready yet</div>}
            {queue.ready.map((wo, i) => (
              <QueueCard key={i} wo={wo} glow />
            ))}
          </div>
        </div>

        {/* Waiting / In Queue */}
        <div style={S.column}>
          <div style={{...S.columnHeader, background:'#64748b'}}>
            <span style={S.columnIcon}>📋</span>
            <span style={S.columnTitle}>In Queue</span>
            <span style={S.columnCount}>{queue.waiting.length}</span>
          </div>
          <div style={S.columnBody}>
            {queue.waiting.length === 0 && <div style={S.emptyCol}>Queue is clear</div>}
            {queue.waiting.map((wo, i) => (
              <QueueCard key={i} wo={wo} />
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={S.footer}>
        <span>Total bikes in shop: {data.total}</span>
        <span>Auto-refreshes every 30 seconds</span>
      </div>
    </div>
  );
}

function QueueCard({ wo, pulse, glow }) {
  const sc = STATUS_CONFIG[wo.status] || {};
  return (
    <div style={{
      ...S.card,
      ...(pulse ? { animation: 'pulse 2s infinite', borderLeft: '4px solid #3b82f6' } : {}),
      ...(glow ? { borderLeft: '4px solid #22c55e', background: '#f0fdf4' } : {}),
    }}>
      <div style={S.cardTop}>
        <span style={S.orderNum}>{wo.order_number}</span>
        <span style={{...S.statusBadge, background: sc.bg, color: sc.color}}>
          {sc.icon} {sc.label}
        </span>
      </div>
      {wo.vehicle && <div style={S.vehicleName}>{wo.vehicle}</div>}
      {wo.title && <div style={S.jobTitle}>{wo.title}</div>}
      <div style={S.cardBottom}>
        {wo.mechanic && <span style={S.mechanic}>👤 {wo.mechanic}</span>}
        {wo.priority === 'urgent' && <span style={S.urgent}>🔴 URGENT</span>}
        {wo.priority === 'high' && <span style={S.high}>🟠 HIGH</span>}
      </div>
    </div>
  );
}

const S = {
  screen: { minHeight:'100vh', background:'#0f172a', display:'flex', flexDirection:'column', fontFamily:'-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif' },
  loadingScreen: { minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'#0f172a', color:'#fff' },

  header: { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'20px 32px', color:'#fff' },
  headerLeft: {},
  headerRight: { textAlign:'right' },
  shopName: { fontSize:28, fontWeight:800, letterSpacing:'-0.5px' },
  shopSub: { fontSize:14, opacity:0.7, marginTop:2 },
  clock: { fontSize:36, fontWeight:800, fontVariantNumeric:'tabular-nums' },
  date: { fontSize:14, opacity:0.7, marginTop:2 },

  columns: { display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:20, padding:'20px 32px', flex:1 },
  column: { background:'#1e293b', borderRadius:16, overflow:'hidden', display:'flex', flexDirection:'column' },
  columnHeader: { padding:'16px 20px', display:'flex', alignItems:'center', gap:10 },
  columnIcon: { fontSize:24 },
  columnTitle: { fontSize:18, fontWeight:800, color:'#fff', flex:1 },
  columnCount: { width:32, height:32, borderRadius:'50%', background:'rgba(255,255,255,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, color:'#fff', fontSize:16 },
  columnBody: { padding:16, flex:1, overflowY:'auto' },
  emptyCol: { textAlign:'center', color:'#475569', padding:40, fontSize:15 },

  card: { background:'#fff', borderRadius:12, padding:16, marginBottom:12, borderLeft:'4px solid transparent' },
  cardTop: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 },
  orderNum: { fontSize:14, fontWeight:800, color:'#3b82f6' },
  statusBadge: { padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700 },
  vehicleName: { fontSize:16, fontWeight:700, color:'#1e293b', marginBottom:4 },
  jobTitle: { fontSize:13, color:'#64748b', marginBottom:8 },
  cardBottom: { display:'flex', gap:8, alignItems:'center' },
  mechanic: { fontSize:12, color:'#64748b', fontWeight:500 },
  urgent: { fontSize:11, fontWeight:800, color:'#ef4444' },
  high: { fontSize:11, fontWeight:800, color:'#f59e0b' },

  footer: { display:'flex', justifyContent:'space-between', padding:'12px 32px', color:'#475569', fontSize:12 },
};
